import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  IconButton,
  OutlineButton,
  SubtleButton,
} from "components/button";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import WarrantyItemsCon from "containers/warrantyItems";
import { useFormik } from "formik";
import { RiArrowLeftSLine } from "react-icons/ri";
import { FiPlus } from "react-icons/fi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TWarranty, TWarrantyList, TWarrantyTargetItem } from "types/warranty";
import WarrantyLine from "./warrantyLine";
import { useTranslation } from 'react-i18next';
import Field from "components/field";
import Switch from "rc-switch";

type TProps = {
  id: string;
  type: "project" | "request" | "property";
};
export default function WarrantyTab(props: TProps) {
  const { t } = useTranslation();
  const tBase = "containers.warrantyTab.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext();

  const [showArchived, setShowArchived] = useState(false);

  const query = useQuery<{
    items: TWarrantyTargetItem[] | null;
    prev_items: TWarrantyTargetItem[] | null;
  }>(
    ["warranty", props.type, props.id],
    async () =>
      await axios
        .get(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/warranty/${props.id}?type=${props.type}
    `
        )
        .then((res) => res.data)
  );

  const filterItems = query.data?.items?.filter((item) => showArchived ? item : !item.archived);
  const filterPrevItems = query.data?.prev_items?.filter((item) => showArchived ? item : !item.archived);

  const populateWarranties = filterItems?.map((item) => (
    <WarrantyLine item={item} key={item.id} parentId={props.id} parentType={props.type} />
  ));

  const populatePrevWarranties = filterPrevItems?.map((item) => (
    <WarrantyLine item={item} key={item.id} parentId={props.id} parentType={props.type} />
  ));

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between border-b py-4">
          <div className="flex items-center">
            <h2>{tr("Warranties")}</h2>

              <div className="ml-10 mr-2">Show Archived</div>
              <Switch
                checked={showArchived}
                onChange={() => setShowArchived(!showArchived)}
              />

          </div>
            <div className="space-x-2">
              <IconButton
                primary
                onClick={() =>
                  modal.setModal({
                    component: (
                      <WarrantyModal id={props.id} type={props.type} />
                    ),
                    label: tr("Warranty"),
                  })
                }
              >
                <FiPlus size={24} />
              </IconButton>
            </div>
        </div>
      </div>
      <div className="space-y-4">{populateWarranties}</div>
      {query.data?.prev_items && (
        <div className="mt-2">
          <div className="flex items-center space-x-2 py-2">
            <span className="text-[#ff4f00] text-xs font-bold">
              {tr("Property Warranty")}{" "}
            </span>
            <div className="flex-1 h-[1px] bg-[#ff4f00]" />
          </div>

          <div className="space-y-4">{populatePrevWarranties}</div>
        </div>
      )}
    </div>
  );
}

const WarrantyModal = (props: TProps) => {
  const { t } = useTranslation();
  const tBase = "containers.warrantyTab.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext(),
    snackbar = useSnackbarContext(),
    queryClient = useQueryClient();

  const [page, setPage] = React.useState("items");
  const [newItemIndices, setNewItemIndices] = React.useState<Set<number>>(new Set());
  const [bulkStartDate, setBulkStartDate] = React.useState<string>("");
  
  // Function to update start dates for all new items
  const updateNewItemsStartDate = (date: string) => {
    if (!formik.values.items || newItemIndices.size === 0) return;
    
    const updatedItems = [...formik.values.items];
    newItemIndices.forEach(index => {
      if (updatedItems[index]) {
        updatedItems[index].start_date = date;
      }
    });
    formik.setFieldValue("items", updatedItems);
  };

  // Handle bulk date change
  const handleBulkDateChange = (date: string) => {
    setBulkStartDate(date);
    updateNewItemsStartDate(date);
  };


  const mutation = useMutation(
    async (e: TWarranty | null) =>
      await axios
        .post(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/warranty/${props.id}
    `,
          { ...e, type: props.type }
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        modal.setModal();
        snackbar.showSnackbar(tr("Warranty updated!"));
      },
    }
  );

  const templates = useQuery<TWarrantyList[] | null>(
    ["warranties"],
    async () =>
      await axios
        .get(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/warranty/template
    `
        )
        .then((res) => res.data)
  );

  const onSubmit = (e: TWarranty) => {
    // Format start_date for all items similar to editWarrantyLineModal
    const formattedItems = e.items?.map(item => ({
      ...item,
      start_date: item.start_date
        ? new Date(item.start_date + " 0:00").toISOString()
        : null,
    })) || [];
    
    mutation.mutate({
      ...e,
      items: formattedItems
    });
  };

  const initialValues = {
    items: [], // Always start with empty items for new warranties
  };

  const formik = useFormik({
    onSubmit,
    initialValues,
    enableReinitialize: true,
  });

  // Form validation - check if all items have required fields filled
  const isFormValid = () => {
    const items = formik.values.items || [];
    if (items.length === 0) return false;
    
    return items.every(item => {
      return item.duration > 0 && 
             item.duration_type && 
             item.label.trim() !== "";
    });
  };

  const getItems = useMutation(
    async (e: { id: string }) =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/warranty/${e.id}
  `
        )
        .then((res) => res.data),
    {
      onSuccess: (data: TWarranty) => {
        // Preserve existing items and append template items at the bottom
        const existingItems = formik.values.items || [];
        const templateItems = (data.items || []).map(item => {
          const { id, ...itemWithoutId } = item;
          return {
            ...itemWithoutId,
            start_date: itemWithoutId.start_date 
              ? new Date(itemWithoutId.start_date).toISOString().split('T')[0]
              : null
          }; // Remove the ID completely and format start_date for display
        });
        const combinedItems = [...existingItems, ...templateItems];
        formik.setFieldValue("items", combinedItems);
        
        // Mark template items as new (indices after existing items)
        const existingCount = existingItems.length;
        const templateIndices = new Set<number>();
        for (let i = existingCount; i < existingCount + templateItems.length; i++) {
          templateIndices.add(i);
        }
        setNewItemIndices(prev => new Set([...prev, ...templateIndices]));
        
        // Apply bulk start date to new template items if set
        if (bulkStartDate) {
          updateNewItemsStartDate(bulkStartDate);
        }
        
        setPage("items");
      },
    }
  );

  const populateTemplates = templates.data?.map((template) => {
    const handleClick = () => {
      getItems.mutate({ id: template.id! });
    };

    return (
      <OutlineButton
        key={template.id}
        onClick={handleClick}
        isLoading={getItems.isLoading}
      >
        {template.label}
      </OutlineButton>
    );
  }) || <span>{tr("No template available.")}</span>;

  return (
    <div className="w-[500px]">
      {page === "items" ? (
        <div>
          {" "}
          <div className="flex items-center justify-between mt-[2px]">
            <OutlineButton primary onClick={() => setPage("templates")}>
              {tr("Select Template")}
            </OutlineButton>
            {newItemIndices.size > 0 && (
              <Field 
                type="date" 
                label={tr("Start Date (updates all items)")}
                name="bulk_start_date"
                value={bulkStartDate}
                onChange={(e: any) => handleBulkDateChange(e.target.value)}
                className="mr-10 mb-[10px] -mt-[10px]"
              />
            )}
          </div>
          {newItemIndices.size > 0 && (<div className="text-xs text-gray-500">
            {tr("If start date is blank, the warranty will automatically start on completion of the request or project. If warranty is created on a property, a manual start date must be set.")}
          </div>)}

          <WarrantyItemsCon 
            formik={formik} 
            newItemIndices={newItemIndices}
            onNewItemIndicesChange={(indices) => {
              setNewItemIndices(indices);
              // Apply bulk start date to any new items that were just added
              if (bulkStartDate) {
                updateNewItemsStartDate(bulkStartDate);
              }
            }}
          />
          <div className="flex justify-end">
            <Button
              primary
              type="submit"
              onClick={() => formik.handleSubmit()}
              isLoading={mutation.isLoading}
              disabled={!isFormValid()}
            >
              {tr("Save Changes")}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div>
            <SubtleButton
              size="small"
              className="!p-0"
              onClick={() => setPage("items")}
            >
              <RiArrowLeftSLine />
              {tr("Back")}
            </SubtleButton>
            <h2 className="py-2">{tr("Templates")}</h2>
          </div>
          <div className="flex flex-col space-y-2">{populateTemplates}</div>
        </div>
      )}
    </div>
  );
};
